import { useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { HiDownload } from 'react-icons/hi';
import { BsTicketPerforated } from 'react-icons/bs';
import { toastInfo, toastSuccess, toastError } from '../utils/toasts';
import config from '../config/config';
import OrderItem from '../types/OrderItem';
import FilterTable, { BaseData } from './Table/Table';
import MenuDropdown from './atoms/MenuDropdown';
import CustomModal from './atoms/CustomModal';
import {
  RootState,
  useDeleteOrderItemMutation,
  useSendOrderEmailMutation,
} from '../store';
import Button from './atoms/Button';
import EditOrderForm from './EditOrderForm';
import useGetParams from '../hooks/useGetParams';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import EmptyTable from './Table/EmptyTable';
import useGetUserRole from '../hooks/useGetUserRole';
import copyLinkToClipboard from '../utils/copyLinkToClipboard';

interface Props {
  orderItems: OrderItem[];
  buttonClick?: () => void;
  loading?: boolean;
}

interface TableOrderItem extends BaseData {
  id: string;
  displayId: string | JSX.Element;
  name: string | JSX.Element;
  personId: string | JSX.Element;
  email: string | JSX.Element;
  ticket_name: string | JSX.Element;
  event_seat_name: string | JSX.Element;
}

const ApprovedOrdersTable: React.FC<Props> = ({ orderItems, buttonClick, loading }) => {
  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState(<div></div>);
  const { event } = useGetSelectedEvent();
  const { token } = useSelector((state: RootState) => state.auth);
  const { eventId, businessId } = useGetParams();
  const role = useGetUserRole();
  const [deleteOrderItem, resultsDelete] = useDeleteOrderItemMutation();
  const [sendEmail] = useSendOrderEmailMutation();

  const handleConfirmDeleteOrder = (item: OrderItem) => {
    toastInfo('Eliminando...');
    deleteOrderItem({
      businessId,
      eventId,
      orderId: item.id,
      itemId: item.item_id,
    }).unwrap()
      .then(() => {
        toastSuccess('Entrada eliminada con éxito');
      })
      .catch(() => {
        toastError('Error al eliminar orden.');
      });
    setShowModal(false);
  };

  const handleClose = () => setShowModal(false);

  const handleDeleteOrder = (item: OrderItem) => {
    setModalBody(
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <p className='text-xl font-medium'>¿Eliminar entrada?</p>
          <p className='text-sm text-text-tertiary'>Al eliminar la entrada quedará inhabilitada y no podrá ser utilizada.</p>
        </div>
        <div className='flex flex-row gap-2'>
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => handleConfirmDeleteOrder(item)}
            className="text-center w-full mt-2"
          >
            Eliminar
          </Button>
        </div>
      </div>,
    );

    setShowModal(true);
  };

  const handleEditItem = (item: OrderItem) => {
    setModalBody(
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2'>
          <p className='text-xl font-medium'>Editar datos</p>
          <p className='text-sm text-text-tertiary'>En los siguientes campos podrás editar los datos que necesites</p>
        </div>
        <EditOrderForm order={item} onSuccess={handleClose} onCancel={handleClose} />
      </div>,
    );

    setShowModal(true);
  };

  const resendEmail = (item: OrderItem) => {
    toastInfo('Enviando correo...');
    sendEmail({
      businessId,
      eventId,
      customId: item.order_custom_id,
    }).unwrap()
      .then(() => {
        toastSuccess('Correo enviado con éxito');
      })
      .catch(() => {
        toastError('Error al enviar correo.');
      });
  };

  const makeDownloadQrRequest = async (item: OrderItem) => {
    if (event?.has_order_qrs) {
      const response = await axios.get(
        `${config.baseURL}/v1/businesses/${businessId}/events/${eventId}/orders/${item.order_id}/qr-pdf`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response;
    }

    const response = await axios.get(
      `${config.baseURL}/v1/businesses/${businessId}/events/${eventId}/orders/${item.order_id}/items/${item.item_id}/qr-pdf`,
      {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response;
  };

  const downloadQr = (item: OrderItem) => {
    toastInfo('Obteniendo PDF...');
    makeDownloadQrRequest(item)
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `QR_Ticket_${item.last_name}_${item.first_name}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        toastSuccess('Descargando PDF');
      })
      .catch((e) => {
        console.error(e);
        toastError('Error al obtener PDF');
      });
  };

  const handleCopyLink = (itemOrder: OrderItem) => {
    const link = `${window.location.origin}/events/${event?.slug}/orders/${itemOrder.order_id}`;
    copyLinkToClipboard(link);
  };

  const dropDownOptions = [
    {
      label: 'Copiar link',
      onClick: handleCopyLink,
      hidden: !event?.seller_can_download_tickets && role === 'SELLER',
    },
    {
      label: 'Descargar QR',
      onClick: downloadQr,
      hidden: !event?.seller_can_download_tickets && role === 'SELLER',
    },
    {
      label: 'Reenviar correo',
      onClick: resendEmail,
    },
    {
      label: 'Editar',
      onClick: handleEditItem,
      hidden: role !== 'ADMIN' && role !== 'ROOT',
    },
    {
      label: 'Eliminar',
      onClick: handleDeleteOrder,
      hidden: role !== 'ADMIN' && role !== 'ROOT',
    },
  ];

  const headCells = [
    {
      id: 'displayId',
      label: 'ID',
    },
    {
      id: 'name',
      label: 'Nombre',
    },
    {
      id: 'personId',
      label: 'DNI',
    },
    {
      id: 'ticket_name',
      label: 'Entrada',
    },
    ...(event?.has_numerated_seats ? [{
      id: 'event_seat_name',
      label: 'Asiento',
    }] : []),
    {
      id: 'used',
      label: '¿Usado?',
    },
  ];

  const tableRows: TableOrderItem[] = orderItems
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .map((item) => ({
      id: item.item_id,
      displayId: <div className='whitespace-nowrap'>{item.order_display_id}</div>,
      name: <div className='whitespace-nowrap'>{item.name}</div>,
      personId: <div className='whitespace-nowrap'>{item.person_id}</div>,
      email: <div className='whitespace-nowrap'>{item.email}</div>,
      ticket_name: <div className='whitespace-nowrap'>{item.ticket_name}</div>,
      event_seat_name: <div className='whitespace-nowrap'>{item.seat_name || '-'}</div>,
      used: item.used_at ? 'Si' : 'No',
    }));

  const actions = (row: TableOrderItem) => (
    <MenuDropdown<OrderItem>
      options={dropDownOptions}
      data={orderItems.find((item) => item.item_id === row.id) || orderItems[0]}
      loading={resultsDelete.isLoading}
    />
  );

  return (
    <div>
      {orderItems.length > 0 ? (
        <FilterTable
          rows={tableRows}
          headCells={headCells}
          actions={role === 'SELLER' && !event?.seller_can_download_tickets ? undefined : actions}
          pagination={{
            pageSize: 10,
            enabled: true,
          }}
          searchEnabled={true}
          customButtons={buttonClick ? [
            <Button variant="secondary" onClick={buttonClick} loading={loading}>
              <div className='flex flex-row gap-2 items-center'>
                <HiDownload className="w-5 h-5" />
                <span className='whitespace-nowrap hidden md:block'>Descargar listado</span>
              </div>
            </Button>,
          ] : []}
          tableId="approved-orders-table"
        />
      ) : (
        <EmptyTable
          title="Aún no hay entradas vendidas"
          info="Aquí aparecerán las entradas que vendas"
          icon={BsTicketPerforated}
          iconColor="text-secondary-500"
        />
      )}

      <CustomModal
        onRequestClose={handleClose}
        isOpen={showModal}
        hideCancelButton={true}
      >
        {modalBody}
      </CustomModal>
    </div>
  );
};

export default ApprovedOrdersTable;
